import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

export function useApproverStatus() {
  return useQuery({ queryKey: ['stockOrders', 'me'], queryFn: async () => (await api.get('/api/stock-orders/me')).data });
}

export function useMyOrders() {
  return useQuery({
    queryKey: ['stockOrders', 'mine'],
    queryFn: async () => (await api.get('/api/stock-orders/mine')).data.orders,
    staleTime: 0,
  });
}

export function useApprovalQueue(status = 'submitted') {
  return useQuery({
    queryKey: ['stockOrders', 'queue', status],
    queryFn: async () => (await api.get(`/api/stock-orders?status=${status}`)).data.orders,
    staleTime: 0,
  });
}

export function useOrder(id) {
  return useQuery({
    queryKey: ['stockOrders', 'detail', String(id)],
    queryFn: async () => (await api.get(`/api/stock-orders/${id}`)).data,
    enabled: !!id,
    staleTime: 0,
  });
}

export function useCreateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post('/api/stock-orders', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stockOrders'] }),
  });
}

export function useSubmitOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/api/stock-orders/${id}/submit`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stockOrders'] }),
  });
}

export function useApproveOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, lines }) => (await api.post(`/api/stock-orders/${id}/approve`, { lines })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stockOrders'] }),
  });
}

export function useRejectOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }) => (await api.post(`/api/stock-orders/${id}/reject`, { reason })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stockOrders'] }),
  });
}

export function useReorderSuggestions() {
  return useQuery({ queryKey: ['stockOrders', 'reorder'], queryFn: async () => (await api.get('/api/stock-orders/reorder-suggestions')).data, staleTime: 0 });
}

export function useStorerooms() {
  return useQuery({ queryKey: ['storerooms'], queryFn: async () => (await api.get('/api/stock-orders/storerooms')).data.storerooms, staleTime: 300000 });
}

export function useSendSupplier() {
  const qc = useQueryClient();
  return useMutation({
    // line_contracts is named explicitly. It used to be missing from this
    // destructure, so the per-line contract overrides the screen collects were
    // quietly thrown away before the request was ever sent.
    // no_approval + call_off_ref: TKT-00071's "No approval required" path.
    // account + line_accounts: TKT-00070's account (GL code) choice.
    mutationFn: async ({ id, supplier_id, line_ids, requester, contract, area, line_contracts, comments, motivation, no_approval, call_off_ref, account, line_accounts }) => (await api.post(`/api/stock-orders/${id}/send-supplier`, { supplier_id, line_ids, requester, contract, area, line_contracts, comments, motivation, no_approval, call_off_ref, account, line_accounts })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stockOrders'] }),
  });
}

// Take an item off a submitted order, or put it back. The line is not deleted:
// it stays visible to the storeman who raised it, struck through, showing who
// removed it.
export function useRemoveOrderLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, line_id, restore }) => (
      await api.post(`/api/stock-orders/${id}/lines/${line_id}/remove`, { restore: !!restore })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stockOrders'] }),
  });
}

// Change how many of an item to buy. The storeman's original figure is kept, so
// the order still shows what he actually asked for.
export function useSetOrderLineQuantity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, line_id, quantity }) => (
      await api.post(`/api/stock-orders/${id}/lines/${line_id}/quantity`, { quantity })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stockOrders'] }),
  });
}

// Put an item on a submitted order that the storeman left off. The line is
// marked with who added it.
export function useAddOrderLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, product_id, storeroom_item_id, quantity }) => (
      await api.post(`/api/stock-orders/${id}/lines`, { product_id, storeroom_item_id, quantity })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stockOrders'] }),
  });
}

// Items an approver can add: both catalogues, ISP included, only those with a
// supplier to put them on a PO. Searches from two characters.
export function useApproverCatalogue(q) {
  const term = (q || '').trim();
  return useQuery({
    queryKey: ['stockOrders', 'approverCatalogue', term],
    queryFn: async () => (await api.get(
      `/api/stock-orders/catalogue/approver?q=${encodeURIComponent(term)}`)).data.items,
    enabled: term.length >= 2,
    staleTime: 60000,
  });
}

export function useTrackingOptions() {
  return useQuery({
    queryKey: ['stockOrders', 'tracking-options'],
    queryFn: async () => (await api.get('/api/stock-orders/xero/tracking-options')).data,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCancelOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/api/stock-orders/${id}/cancel`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stockOrders'] }),
  });
}

// Consumables that can actually be bought: storeroom items with a real
// supplier price behind them. Items with no price list, or with prices that
// disagree on the unit (per-each vs per-pack), are absent by design.
export function useConsumableCatalogue(q) {
  return useQuery({
    queryKey: ['stockOrders', 'consumableCatalogue', q || ''],
    queryFn: async () => (await api.get(
      `/api/stock-orders/catalogue/consumables${q ? `?q=${encodeURIComponent(q)}` : ''}`)).data.items,
    staleTime: 60000,
  });
}

// What the company already holds of one ordered item, storeroom by storeroom.
// Fetched only when a line is opened — one call per item, not per order.
export function useLineAvailability(lineId) {
  return useQuery({
    queryKey: ['stockOrders', 'availability', String(lineId || '')],
    queryFn: async () => (await api.get(`/api/stock-orders/lines/${lineId}/availability`)).data,
    enabled: !!lineId,
    staleTime: 60 * 1000,
  });
}

// Cancel a supplier PO that has already gone to Xero / ApprovalMax. Deletes it
// in Xero and marks it cancelled here. drop_lines takes its lines off the order
// (it stays approved); otherwise they unlock and the order reopens.
export function useCancelPurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, po_id, drop_lines = false }) => (await api.post(`/api/stock-orders/${id}/purchase-orders/${po_id}/cancel`, { drop_lines })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stockOrders'] }),
  });
}
